/*
 * SBC native cart: the UpCart behaviours the theme drawer lacks (2026-09-24).
 * Layout lives in Liquid (snippets/cart-drawer.liquid, sbc-drawer-line, sbc-cart-rewards, sbc-cart-upsells) and
 * re-renders through the Section Rendering API on every cart change. This file only wires events.
 */
(() => {
  if (window.SbcCart) return;

  const drawer = () => document.querySelector('cart-drawer');
  const cfgEl = document.getElementById('SbcCartConfig');
  const cfg = Object.assign(
    { sticky: false, klaviyoAtc: false, openOnAdd: true },
    cfgEl ? JSON.parse(cfgEl.textContent) : {}
  );
  const SECTIONS = 'cart-drawer,cart-counter';
  const origFetch = window.fetch.bind(window);

  // Re-render the drawer from a response that carries `sections`, or fetch them.
  const render = (payload, open) => {
    const d = drawer();
    if (!d || !payload || !payload.sections) return;
    d.getSectionsToRender().forEach(section => {
      const el = document.querySelector(section.id);
      const html = payload.sections[section.section];
      if (!el || !html) return;
      el.innerHTML = d.getSectionInnerHTML(html, section.selector);
      d.updateCartParts(
        d.getCartElements(payload, section, ['[data-cart-title]', '[data-cart-shipping-bar]', '[data-cart-summary]']),
        ['[data-cart-title]', '[data-cart-shipping-bar]', '[data-cart-summary]']
      );
    });
    d.recommendationsPerformed = false;
    if (open) d.open();
    else if (d.classList.contains('is-visible')) d.performRecommendations();
    afterRender();
  };

  const refresh = open =>
    origFetch(`${window.Shopify?.routes?.root || '/'}?sections=${SECTIONS}`)
      .then(r => r.json())
      .then(sections => render({ sections }, open));

  const post = (url, body) =>
    origFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(Object.assign(body, { sections: SECTIONS, sections_url: window.location.pathname }))
    }).then(r => r.json());

  // ---- 2. Reward gifts: Liquid says what to add/remove; apply it once per distinct state.
  let lastSync = '';
  const syncGifts = () => {
    const el = document.querySelector('[data-sbc-gift-sync]');
    if (!el) return (lastSync = '');
    const spec = el.getAttribute('data-sbc-gift-sync');
    if (!spec || spec === lastSync) return; // a failed add must not loop
    lastSync = spec;
    const updates = {};
    spec.split(/\s+/).forEach(pair => {
      const [id, qty] = pair.split(':');
      if (id) updates[id] = Number(qty);
    });
    post(window.routes.cart_update_url, { updates })
      .then(state => render(state, false))
      .catch(() => {});
  };

  // ---- 3. Sticky button + badge.
  const sticky = () => {
    const btn = document.querySelector('[data-sbc-sticky]');
    if (!btn) return;
    btn.hidden = !cfg.sticky;
    const badge = document.querySelector('.cart-count-badge');
    const count = document.querySelector('[data-sbc-sticky-count]');
    if (badge && count) {
      count.textContent = badge.textContent.trim();
      count.hidden = badge.classList.contains('hidden');
    }
  };

  // Promo field stays open across re-renders while the shopper is using it (2026-10-05 rebuild).
  let promoOpen = false;
  const promo = () => {
    const form = document.querySelector('[data-sbc-promo-form]');
    const toggle = document.querySelector('[data-sbc-promo-toggle]');
    if (!form || !toggle) return;
    form.hidden = !promoOpen;
    toggle.hidden = promoOpen;
    toggle.setAttribute('aria-expanded', String(promoOpen));
  };

  const afterRender = () => {
    syncGifts();
    sticky();
    promo();
  };

  // ---- 4. Open the drawer on any add-to-cart the theme did not make itself (Replo, Kaching, apps).
  const isAdd = url => /\/cart\/add(\.js)?(\?|$)/.test(String(url));
  const ownRequest = body =>
    body instanceof FormData ? body.has('sections') : typeof body === 'string' && body.includes('"sections"');

  const onAdded = () => {
    if (cfg.klaviyoAtc) klaviyo();
    if (cfg.openOnAdd) refresh(true);
  };

  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input && input.url;
    const p = origFetch(input, init);
    if (isAdd(url) && !ownRequest(init && init.body)) {
      p.then(r => r.ok && onAdded()).catch(() => {});
    }
    return p;
  };

  const xOpen = XMLHttpRequest.prototype.open;
  const xSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._sbcAdd = isAdd(url);
    return xOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    if (this._sbcAdd && !ownRequest(body)) {
      this.addEventListener('load', () => this.status >= 200 && this.status < 300 && onAdded());
    }
    return xSend.apply(this, arguments);
  };

  // Klaviyo "Added to Cart", ported from UpCart's aboveHeader script. UpCart's copy read an
  // undeclared `item` and threw before pushing; this one does not.
  const klaviyo = () => {
    origFetch('/cart.js')
      .then(r => r.json())
      .then(data => {
        (window._learnq = window._learnq || []).push([
          'track',
          'Added to Cart',
          {
            total_price: data.total_price / 100,
            $value: data.total_price / 100,
            total_discount: data.total_discount,
            original_total_price: data.original_total_price / 100,
            items: data.items
          }
        ]);
      })
      .catch(() => {});
  };

  // ---- 5. Delegated clicks inside the drawer (its markup is replaced on every render).
  const discountCodes = () =>
    Array.from(document.querySelectorAll('.sbc-discount__code')).map(li => li.dataset.code);

  const applyDiscount = codes => {
    const err = document.querySelector('[data-sbc-discount-error]');
    return post(window.routes.cart_update_url, { discount: codes.join(',') })
      .then(state => {
        render(state, false);
        return state;
      })
      .catch(() => err && (err.textContent = 'Could not apply that code.'));
  };

  // Cart-level subscription: move every line in `plans` (variant id → plan id) on or off its plan.
  // One /cart/change per line, re-reading the cart each time because a plan change can merge lines
  // and shift indexes. Capped at 30 steps; the drawer re-renders once at the end.
  const setPlans = async (plans, on) => {
    for (let step = 0; step < 30; step++) {
      const cart = await origFetch(`${window.routes.cart_url}.js`).then(r => r.json());
      const i = cart.items.findIndex(it => plans[it.variant_id] && (on ? !it.selling_plan_allocation : !!it.selling_plan_allocation));
      if (i === -1) break;
      const it = cart.items[i];
      const res = await origFetch(`${window.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: i + 1, quantity: it.quantity, selling_plan: on ? plans[it.variant_id] : null })
      });
      if (!res.ok) break;
    }
    return refresh(false);
  };

  const busy = new Set(); // variant ids with an add in flight: one request per click burst

  document.addEventListener('click', event => {
    const t = event.target;

    const toggle = t.closest('[data-sbc-promo-toggle]');
    if (toggle) {
      promoOpen = true;
      promo();
      document.querySelector('[data-sbc-discount-input]')?.focus();
      return;
    }

    const add = t.closest('[data-sbc-add]');
    if (add) {
      const id = add.dataset.sbcAdd;
      if (busy.has(id)) return;
      busy.add(id);
      add.disabled = true;
      add.setAttribute('aria-busy', 'true');
      post(window.routes.cart_add_url, { items: [{ id: Number(id), quantity: 1, properties: { _sbcUpsell: '1' } }] })
        .then(state => {
          if (!state || !state.sections) throw new Error(state && state.description);
          render(state, false);
        })
        .catch(() => {
          add.disabled = false;
          add.removeAttribute('aria-busy');
          add.textContent = 'Unavailable';
        })
        .finally(() => busy.delete(id));
      return;
    }

    const apply = t.closest('[data-sbc-discount-apply]');
    if (apply) {
      const input = document.querySelector('[data-sbc-discount-input]');
      const code = input && input.value.trim();
      if (!code) return;
      apply.disabled = true;
      applyDiscount(discountCodes().concat(code)).then(state => {
        const codes = (state && state.discount_codes) || [];
        const hit = codes.find(c => c.code.toLowerCase() === code.toLowerCase());
        const err = document.querySelector('[data-sbc-discount-error]');
        if (hit && hit.applicable) promoOpen = false;
        else if (err) err.textContent = `${code} is not valid for this cart.`;
        promo();
      });
      return;
    }

    const remove = t.closest('[data-sbc-discount-remove]');
    if (remove) {
      const code = remove.closest('.sbc-discount__code').dataset.code;
      applyDiscount(discountCodes().filter(c => c !== code));
      return;
    }

    const off = t.closest('[data-sbc-unsubscribe-all]');
    if (off) {
      if (off.disabled) return;
      off.disabled = true;
      setPlans(JSON.parse(off.dataset.plans), false).catch(() => (off.disabled = false));
      return;
    }

    const all = t.closest('[data-sbc-subscribe-all]');
    if (all) {
      if (all.disabled) return;
      all.disabled = true;
      setPlans(JSON.parse(all.dataset.plans), true).catch(() => (all.disabled = false));
      return;
    }

    const sub = t.closest('[data-sbc-subscribe]');
    if (sub) {
      if (sub.disabled) return;
      sub.disabled = true;
      post(window.routes.cart_change_url, {
        id: sub.dataset.key,
        quantity: Number(sub.dataset.qty),
        selling_plan: Number(sub.dataset.plan)
      })
        .then(state => render(state, false))
        .catch(() => (sub.disabled = false));
      return;
    }

    const checkout = t.closest('[data-sbc-checkout]');
    if (checkout) {
      const terms = document.querySelector('[data-sbc-terms]');
      if (terms && !terms.checked) {
        event.preventDefault();
        document.querySelector('[data-sbc-terms-warning]').hidden = false;
      }
      return;
    }

    if (t.closest('[data-sbc-sticky]')) {
      drawer()?.open(t.closest('[data-sbc-sticky]'));
      return;
    }

    // Any other link to /cart opens the drawer instead, as UpCart did.
    const link = t.closest('a[href]');
    if (link && !link.closest('cart-drawer') && drawer() && /^\/cart\/?$/.test(new URL(link.href, location.href).pathname)) {
      event.preventDefault();
      drawer().open(link);
    }
  });

  // Capture phase: the plan <select> sits inside cart-drawer-items, whose CartItems treats every
  // change as a quantity update. Stop it there and change the selling plan instead.
  document.addEventListener(
    'change',
    event => {
      const planAll = event.target.closest && event.target.closest('[data-sbc-plan-all]');
      if (planAll) {
        event.stopPropagation();
        planAll.disabled = true;
        setPlans(JSON.parse(planAll.dataset.plans), planAll.value === 'on').catch(() => (planAll.disabled = false));
        return;
      }
      const plan = event.target.closest && event.target.closest('[data-sbc-plan]');
      if (!plan) return;
      event.stopPropagation();
      // Real selling plan or one-time (null). Shopify re-prices the line; the drawer re-renders from it.
      plan.disabled = true;
      post(window.routes.cart_change_url, {
        id: plan.dataset.key,
        quantity: Number(plan.dataset.qty),
        selling_plan: plan.value ? Number(plan.value) : null
      })
        .then(state => render(state, false))
        .catch(() => (plan.disabled = false));
    },
    true
  );

  document.addEventListener('change', event => {
    if (!event.target.matches('[data-sbc-terms]')) return;
    const warn = document.querySelector('[data-sbc-terms-warning]');
    if (warn) warn.hidden = event.target.checked;
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Enter' && event.target.matches('[data-sbc-discount-input]')) {
      event.preventDefault();
      document.querySelector('[data-sbc-discount-apply]')?.click();
    }
  });

  // Quantity changes and upsell adds go through cart.js / cart-drawer.js, not through here.
  // Watch the drawer body so gifts and the sticky badge follow those renders too.
  const watch = () => {
    const body = document.getElementById('CartDrawer-Body');
    const summary = document.querySelector('[data-cart-summary]');
    const mo = new MutationObserver(debounce(afterRender, 50));
    [body, summary].forEach(el => el && mo.observe(el, { childList: true }));
    const bar = document.querySelector('[data-cart-shipping-bar]');
    if (bar) mo.observe(bar, { childList: true });
  };

  window.SbcCart = { refresh, render };
  watch();
  afterRender();
})();
